import { Location, isPlatformBrowser } from '@angular/common';
import { computed, DestroyRef, effect, inject, Injectable, Injector, PLATFORM_ID, signal, untracked } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  NavigationCancel,
  NavigationEnd,
  NavigationError,
  NavigationSkipped,
  NavigationStart,
  ROUTER_CONFIGURATION,
  Router,
} from '@angular/router';
import { carriesOidcSecret, excludedWhileShown, isOidcCallback, taintsDocument } from './analytics-boundary';
import { CONSENT_STORAGE_KEY, ConsentStore } from './consent.store';
import { ContentsquareBrowser } from './contentsquare-browser';

/** Contentsquare's cookies, and most of its storage keys, start with this. */
const TAG_PREFIX = '_cs_';
/**
 * Storage the tag writes without that prefix (gate F4): `csAnalysisEventsPersisted` holds events
 * a failed beacon left behind, and a later start of the tag sends them. Found in release 15.263.3;
 * re-check on a tag upgrade.
 */
const UNPREFIXED_TAG_STORAGE_KEYS = ['csAnalysisEventsPersisted'] as const;
/** Written by the `optout` command; it stops a later tag from collecting until it is removed. */
const OPT_OUT_COOKIE = '_cs_optout';

/**
 * Story 50.6 — loads the Contentsquare tag, and only when every condition holds:
 *   - this build has a tag ID (production) and the visitor accepted analytics cookies;
 *   - the document has never shown a tainting page (`analytics-boundary.ts`), its same-origin
 *     referrer is not one either, and it has not left the OIDC callback with the code still in
 *     its history entry;
 *   - the current page is not excluded (the OIDC callback while it is the current URL);
 *   - no navigation is in flight (the next URL is not known yet).
 *
 * The choice is per DEVICE, so every change of it is followed here, wherever it came from: a
 * choice in this tab, a `storage` event from another tab, or a page restored from the
 * back/forward cache. Every "no" removes the tag's cookies and storage (keeping `_cs_optout`),
 * whether or not this document ever loaded the tag, and a tag running in this document is opted
 * out. A "yes" after a "no" removes `_cs_optout` so the next document collects again (the web
 * tag has no opt-back-in command); a stored "yes" read at start never touches it, so an opt-out
 * made through Contentsquare's own mechanism is respected.
 *
 * Once the tag is injected it stays for the life of the document: there is no unload. From then
 * on `admit()` (the route guard) turns an in-app navigation into a tainting page into a full
 * page load. That cancel is clean only while the router updates the URL after guards
 * (`urlUpdateStrategy: 'deferred'`, Angular's default), so `start()` refuses to run otherwise.
 *
 * Started once from an app initializer, so it sees the boot URL before any redirect and every
 * router event from the initial navigation on. Does nothing on the server.
 */
@Injectable({ providedIn: 'root' })
export class ContentsquareService {
  private readonly consent = inject(ConsentStore);
  private readonly browser = inject(ContentsquareBrowser);
  private readonly router = inject(Router);
  private readonly routerConfig = inject(ROUTER_CONFIGURATION, { optional: true });
  private readonly location = inject(Location);
  private readonly injector = inject(Injector);
  private readonly destroyRef = inject(DestroyRef);
  private readonly isBrowser = isPlatformBrowser(inject(PLATFORM_ID));

  private started = false;
  /** Asked to inject (set at once, so the guard sees it); reset if the SDK import then declines. */
  private requested = false;
  /** The `<script>` was appended. Never resets: a loaded tag cannot be unloaded. */
  private injected = false;
  /** `optout` was pushed to this document's tag since the last "yes". */
  private optedOut = false;
  /** The effective choice last acted on (by the effect, or by the expiry check); null before the first. */
  private lastGranted: boolean | null = null;

  /** This document has shown a tainting page, or arrived from one. Written only here. */
  private readonly taintedState = signal(false);
  readonly tainted = this.taintedState.asReadonly();
  private readonly currentPath = signal<string | null>(null);
  private readonly navigating = signal(false);

  /** The current page may not be seen by the tag, or is not known yet. */
  readonly excludedNow = computed(() => {
    const path = this.currentPath();
    return path === null || excludedWhileShown(path);
  });

  start(): void {
    if (!this.isBrowser || this.started || !this.consent.configured()) return;
    if (this.routerConfig?.urlUpdateStrategy === 'eager') {
      // With 'eager' the address bar changes before guards run, so the tag would see a tainting
      // URL before `admit()` could cancel. Fail closed rather than record it.
      console.error('Contentsquare stays off: the router updates the URL before guards (urlUpdateStrategy "eager").');
      return;
    }
    this.started = true;
    this.consent.load();
    this.destroyRef.onDestroy(this.browser.onStoredChoiceChange(CONSENT_STORAGE_KEY, () => this.consent.load()));

    const referrer = this.browser.sameOriginReferrer();
    if (
      taintsDocument(this.browser.pathname()) ||
      (referrer !== null && (taintsDocument(referrer.path) || (isOidcCallback(referrer.path) && carriesOidcSecret(referrer.search))))
    ) {
      this.taintedState.set(true);
    }
    // Normally the initial navigation has not run yet; this covers a late start.
    if (this.router.navigated) this.currentPath.set(this.router.url);

    this.router.events.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((event) => {
      if (event instanceof NavigationStart) {
        this.navigating.set(true);
        // A consent that expired while this tab stayed open ends here, like on a new page.
        this.expireBeforeHistoryWrite();
        // LEAVING the callback while its entry still holds the code (sign-in failed before
        // `tryLogin()` stripped it): Back would replay that URL to a tag loaded later. Not the
        // navigation INTO the callback (gate F3): it starts with the code in the URL on every
        // sign-in, and `tryLogin()` only runs once the callback page is active.
        if (!isOidcCallback(event.url) && isOidcCallback(this.browser.pathname()) && carriesOidcSecret(this.browser.search())) {
          this.taintedState.set(true);
        }
      } else if (event instanceof NavigationEnd) {
        this.onShown(event.urlAfterRedirects);
        this.navigating.set(false);
      } else if (event instanceof NavigationCancel || event instanceof NavigationError || event instanceof NavigationSkipped) {
        this.navigating.set(false);
      }
    });

    effect(
      () => {
        const granted = this.consent.granted();
        const blocked = this.tainted() || this.excludedNow() || this.navigating();
        untracked(() => {
          if (granted !== this.lastGranted) this.onChoiceChange(granted);
          if (granted && !blocked && !this.requested) this.requestTag();
        });
      },
      { injector: this.injector },
    );
  }

  /**
   * The route guard's answer for a tainting page. Without the tag in this document the in-app
   * navigation proceeds (and taints the document when it lands). With it, the navigation is
   * cancelled before the URL changes and repeated as a full page load, so the tag never sees it.
   */
  admit(url: string): boolean {
    if (!this.isBrowser || !this.requested) return true;
    this.browser.assign(this.location.prepareExternalUrl(url));
    return false;
  }

  /**
   * Drops a "yes" that passed its 13 months while this document stayed open, and withdraws at
   * once (followup F2-R). The router writes the address bar later in the same navigation and a
   * running tag reports that write as a pageview, so the stop cannot wait for the effect. A fresh
   * "yes", even one the browser failed to save, stays: `expireStale()` reads the record in memory.
   */
  private expireBeforeHistoryWrite(): void {
    if (!this.consent.granted()) return;
    this.consent.expireStale();
    if (!this.consent.granted()) this.onChoiceChange(false);
  }

  private onChoiceChange(granted: boolean): void {
    const previous = this.lastGranted;
    this.lastGranted = granted;
    if (granted) {
      // A "yes" after a "no" (here or in another tab): let the next tag collect again. Not on
      // the first run, so a `_cs_optout` set through Contentsquare's own link is kept.
      if (previous === false) this.browser.expireCookie(OPT_OUT_COOKIE);
      this.optedOut = false;
      return;
    }
    if (this.injected && !this.optedOut) {
      // `data-collection:stop` FIRST (gate F1): the tag's pageview producer has no opt-out hook,
      // so a page restored from the back/forward cache would still send a pageview and recreate
      // the cookies. `optout` then stops the command processor itself (a stop sent after it is
      // ignored), writes `_cs_optout` for 13 months and deletes the tag's other cookies on the
      // current domain. Verified against tag release 15.263.3 by the gate's executed probe; the
      // stop command is not in Contentsquare's public reference, so re-check it on a tag upgrade.
      this.browser.push(['data-collection:stop']);
      this.browser.push(['optout']);
      this.optedOut = true;
    }
    // Every "no", tag or not: what an earlier "yes" left may sit on a parent domain or in storage.
    for (const name of this.browser.cookieNames()) {
      if (name.startsWith(TAG_PREFIX) && name !== OPT_OUT_COOKIE) this.browser.expireCookie(name);
    }
    this.browser.removeStorageKeys(TAG_PREFIX);
    for (const key of UNPREFIXED_TAG_STORAGE_KEYS) this.browser.removeStorageKeys(key);
  }

  private onShown(url: string): void {
    if (taintsDocument(url)) {
      this.taintedState.set(true);
      // Unreachable while every tainting route carries the guard (the route-tree spec pins it).
      // If a route ever slips through, start a fresh document rather than let the tag stay.
      if (this.requested) this.browser.reload();
    }
    this.currentPath.set(url);
  }

  private requestTag(): void {
    const tagId = this.consent.tagId();
    if (tagId === null) return;
    this.requested = true;
    this.browser
      .loadSdk()
      .then((injectContentsquareScript) => {
        // The chunk loads asynchronously: check again that nothing changed meanwhile. If it did,
        // no tag exists, so forget the request and let a later "yes" ask again.
        if (!this.consent.granted() || this.tainted() || this.excludedNow()) {
          this.requested = false;
          return;
        }
        this.injected = true;
        injectContentsquareScript({ clientId: tagId, async: true, defer: false });
      })
      .catch(() => {
        // Blocked by an ad blocker or the network: the site works the same without analytics.
        this.requested = false;
      });
  }
}
